import type { ReactNode } from 'react';
import {
  Banner,
  BannerDescription,
  DataTable,
  DataTableHeader,
  DataTableHeaderActions,
  DataTableHeaderActionsLeftSection,
  DataTableHeaderActionsRightSection,
  DataTableHeaderColumnsMenu,
  DataTableHeaderRow,
  DataTablePagination,
  DataTableTable,
  Panel,
  PanelInset,
  type DataTableColumnsSettingsConfig,
  type DataTableProps,
} from '@customerio/pluma-components/react';
import type { Customer } from '@/services/db/customer/entity';
import { isEmptyString } from '@/utils/string';
import { ClearSelectionLink, SelectAllLink } from './selection-links';
import { TableListCount } from './table-list-count';
import { usePeopleTable } from './use-people-table';
import * as styles from './people-table.css';

export type PeopleTableProps = {
  people?: readonly Customer[];
  isLoading?: boolean;
  /** Profiles queued for deletion: greyed out and unselectable until the list refetches. */
  pendingDeletionIds?: ReadonlySet<string>;

  /** Every known attribute name — one dynamic column each. */
  allAttributes: readonly string[];
  /** Which `custom_*` attribute columns are visible. */
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  /** Columns-menu paging/search wiring (attribute catalog is server-paged). */
  columnsSettings?: DataTableColumnsSettingsConfig;

  /** Server-side pagination: `page`, `pageSize`, `totalItemCount` (`-1` = unknown), `onPageChange`. */
  pagination: NonNullable<DataTableProps['pagination']>;

  /** `journeys/people/edit` — row selection is hidden entirely without it. */
  canEdit: boolean;
  rowSelection: Record<string, boolean>;
  onRowSelectionChange: (selection: Record<string, boolean>) => void;

  /** Select-all across the whole result set (chosen, or implied by checking every row). */
  allSelected: boolean;
  /** Total matching profiles; `-1` when the backend reports the total as unknown. */
  totalCount: number;
  /** Number of loaded rows currently checked. */
  selectedCount: number;
  onClearSelection: () => void;
  onSelectAll: () => void;
  selectAllDisabled: boolean;
  /** No search term and no filters — hides the cross-page select-all link. */
  noFiltersApplied: boolean;

  /** Masquerade/redaction: the created_at column renders `*****`. */
  shouldRedactCreatedAt?: boolean;

  /** A search term or condition filter is active (drives the no-match empty state). */
  hasFilters: boolean;
  /** The API says there are results but hid them (sensitive-attribute filtering). */
  hasHiddenResults: boolean;
  /** The active email search term — switches the no-match copy to "search". */
  email?: string;

  /** The search/filter rows above the table (the page supplies these). */
  searchSlot?: ReactNode;
  /** API degradation message shown as a warning banner. */
  message?: string | null;
  /** Bulk-action controls next to the count (the bulk-actions layer supplies these). */
  actionsSlot?: ReactNode;
  /** The "Add people" call to action for the no-filters empty state. */
  addPeopleAction?: ReactNode;
};

const getRowId = (person: Customer) => person.id;

/**
 * The People list table: fixed unsubscribed/email/created_at columns plus one
 * visibility-controlled column per profile attribute, row selection with
 * cross-page select-all, server-side pagination, and the layered empty states.
 * Presentational — data, selection state, and URL state live with the caller.
 */
export function PeopleTable(props: PeopleTableProps) {
  const {
    data,
    columns,
    getRowCanSelect,
    paginationConfig,
    emptyState,
    emptyStateComponent,
    unknownTotal,
    hasAnySelection,
    peopleToSelectCount,
    peopleSingular,
    peoplePlural,
    peopleTitlePlural,
  } = usePeopleTable(props);

  return (
    <DataTable
      className={styles.peopleTable}
      caption={peopleTitlePlural}
      data={data}
      columns={columns}
      withPagination
      pagination={paginationConfig}
      withRowSelection={props.canEdit}
      getRowCanSelect={getRowCanSelect}
      rowSelection={props.rowSelection}
      onRowSelectionChange={props.onRowSelectionChange}
      getRowId={getRowId}
      emptyState={emptyState}
      emptyStateComponent={emptyStateComponent}
      isLoading={props.isLoading === true}
      withColumnsSettings
      columnsSettings={props.columnsSettings}
      columnVisibility={props.columnVisibility}
      onColumnVisibilityChange={props.onColumnVisibilityChange}
    >
      <Panel>
        <DataTableHeader>
          {props.searchSlot}

          {props.message == null || isEmptyString(props.message) ? null : (
            <DataTableHeaderRow>
              <Banner variant="caution">
                <BannerDescription>{props.message}</BannerDescription>
              </Banner>
            </DataTableHeaderRow>
          )}

          <DataTableHeaderRow>
            <DataTableHeaderActions>
              <DataTableHeaderActionsLeftSection>
                <TableListCount
                  allSelected={props.allSelected}
                  totalCount={props.totalCount}
                  selectedCount={props.selectedCount}
                  singularName={peopleSingular}
                />

                {props.actionsSlot}

                {hasAnySelection ? <ClearSelectionLink onClearSelection={props.onClearSelection} /> : null}

                {!props.noFiltersApplied && !props.allSelected && !props.hasHiddenResults ? (
                  <SelectAllLink
                    onSelectAll={props.onSelectAll}
                    isDisabled={props.selectAllDisabled}
                    unknownTotal={unknownTotal}
                    peopleToSelectCount={peopleToSelectCount}
                    peoplePlural={peoplePlural}
                  />
                ) : null}
              </DataTableHeaderActionsLeftSection>
              <DataTableHeaderActionsRightSection>
                <DataTableHeaderColumnsMenu />
                <DataTablePagination mt="0" />
              </DataTableHeaderActionsRightSection>
            </DataTableHeaderActions>
          </DataTableHeaderRow>
        </DataTableHeader>

        <PanelInset>
          <DataTableTable />
        </PanelInset>
      </Panel>

      <DataTablePagination mt="200" />
    </DataTable>
  );
}
